/**
 * Feature Gate Components
 * Conditionally render UI elements based on tier access
 */

import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTier } from '../contexts/TierContext'
import { buildBillingUpgradeUrl } from '../utils/billingNavigation'
import { InlineUpgradePrompt, UpgradePrompt, type UpgradeContext } from './UpgradePrompt'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip'
import { Lock, Zap } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export interface FeatureGateProps {
  feature: string
  children: React.ReactNode
  fallback?: React.ReactNode
  upgradeContext?: UpgradeContext
  showUpgradePrompt?: boolean
  fallbackType?: 'hidden' | 'disabled' | 'upgrade-prompt' | 'custom'
  className?: string
}

/**
 * Main Feature Gate Component
 * Controls access to features based on user tier
 */
export function FeatureGate({
  feature,
  children,
  fallback,
  upgradeContext = 'feature_access',
  showUpgradePrompt: _showUpgradePrompt = false,
  fallbackType = 'hidden',
  className = ""
}: FeatureGateProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { canAccess, getCurrentTier, getNextTier } = useTier()
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const { t } = useTranslation()

  const hasAccess = canAccess(feature)

  if (hasAccess) {
    return <div className={className}>{children}</div>
  }

  // Handle different fallback types
  switch (fallbackType) {
    case 'hidden':
      return null

    case 'disabled':
      return (
        <div className={`${className} opacity-50 pointer-events-none relative`}>
          {children}
          <div className="absolute inset-0 bg-gray-100/50 flex items-center justify-center">
            <Badge variant="secondary" className="flex items-center gap-1">
              <Lock className="h-3 w-3" />
{t('featureGate.upgradeRequired', { ns: 'common' }) as string}
            </Badge>
          </div>
        </div>
      )

    case 'upgrade-prompt':
      return (
        <>
          <InlineUpgradePrompt
            context={upgradeContext}
            currentTier={getCurrentTier()}
            nextTier={getNextTier() || 'pro'}
            onUpgrade={() => setShowUpgradeModal(true)}
            className={className}
          />
          <UpgradePrompt
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            onUpgrade={() => {
              setShowUpgradeModal(false)
              navigate(
                buildBillingUpgradeUrl({
                  returnPath: `${location.pathname}${location.search}`,
                  source: 'feature_gate_prompt',
                })
              )
            }}
            context={upgradeContext}
            currentTier={getCurrentTier()}
            nextTier={getNextTier() || 'pro'}
          />
        </>
      )

    case 'custom':
      return <div className={className}>{fallback}</div>

    default:
      return null
  }
}

/**
 * Button Feature Gate
 * Wraps buttons with tier-based access control
 */
export function FeatureGateButton({
  feature,
  children,
  upgradeContext = 'feature_access',
  tooltipText,
  showBadge = true,
  className = "",
  ...buttonProps
}: {
  feature: string
  children: React.ReactNode
  upgradeContext?: UpgradeContext
  tooltipText?: string
  showBadge?: boolean
  className?: string
} & React.ComponentProps<typeof Button>) {
  const navigate = useNavigate()
  const location = useLocation()
  const { canAccess, getCurrentTier, getNextTier } = useTier()
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  const hasAccess = canAccess(feature)

  if (hasAccess) {
    return (
      <Button className={className} {...buttonProps}>
        {children}
      </Button>
    )
  }

  const handleUpgradeClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setShowUpgradeModal(true)
  }

  const button = (
    <>
      <Button
        {...buttonProps}
        className={`${className} relative`}
        variant="outline"
        onClick={handleUpgradeClick}
      >
        <div className="flex items-center gap-2">
          {children}
          {showBadge && <Lock className="h-3 w-3" />}
        </div>
      </Button>
      <UpgradePrompt
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={() => {
          setShowUpgradeModal(false)
          navigate(
            buildBillingUpgradeUrl({
              returnPath: `${location.pathname}${location.search}`,
              source: 'feature_gate',
            })
          )
        }}
        context={upgradeContext}
        currentTier={getCurrentTier()}
        nextTier={getNextTier() || 'pro'}
      />
    </>
  )

  if (tooltipText) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {button}
          </TooltipTrigger>
          <TooltipContent>
            <p>{tooltipText}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return button
}

/**
 * Resource Limit Gate
 * Controls access based on resource limits (spaces, memories, etc.)
 */
export function ResourceLimitGate({
  resourceType,
  currentCount,
  children,
  fallbackType = 'upgrade-prompt',
  upgradeContext = 'memory_limit',
  className = ""
}: {
  resourceType: string
  currentCount: number
  children: React.ReactNode
  fallbackType?: 'hidden' | 'disabled' | 'upgrade-prompt'
  upgradeContext?: UpgradeContext
  className?: string
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { getResourceLimits, getCurrentTier, getNextTier, loading } = useTier()
  const [limitInfo, setLimitInfo] = useState<any>(null)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [isChecking, setIsChecking] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    // Don't check limits while tier info is still loading
    if (loading || isChecking) {
      return
    }

    async function checkLimits() {
      try {
        setIsChecking(true)
        const info = await getResourceLimits(resourceType, currentCount)
        setLimitInfo(info)
      } catch (error) {
        console.warn(`Failed to check ${resourceType} usage limits:`, error)
        // Fail silently for usage warnings - better to show no warning than break the UI
        setLimitInfo(null)
      } finally {
        setIsChecking(false)
      }
    }
    
    checkLimits()
  }, [resourceType, currentCount, getResourceLimits, loading, isChecking])

  // Show children while loading or if no limit info (fail open)
  if (loading || isChecking || !limitInfo) {
    return <div className={className}>{children}</div>
  }

  const hasCapacity = limitInfo.limit_status.status === 'unlimited' || 
                     limitInfo.limit_status.remaining > 0

  if (hasCapacity) {
    return <div className={className}>{children}</div>
  }

  // Handle limit exceeded
  switch (fallbackType) {
    case 'hidden':
      return null

    case 'disabled':
      return (
        <div className={`${className} opacity-50 pointer-events-none relative`}>
          {children}
          <div className="absolute inset-0 bg-gray-100/50 flex items-center justify-center">
            <Badge variant="destructive" className="flex items-center gap-1">
              <Lock className="h-3 w-3" />
              {t('featureGate.limitReached', { ns: 'common' }) as string}
            </Badge>
          </div>
        </div>
      )

    case 'upgrade-prompt':
      return (
        <>
          <InlineUpgradePrompt
            context={upgradeContext}
            currentTier={getCurrentTier()}
            nextTier={getNextTier() || 'pro'}
            onUpgrade={() => setShowUpgradeModal(true)}
            className={className}
          />
          <UpgradePrompt
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            onUpgrade={() => {
              setShowUpgradeModal(false)
              navigate(
                buildBillingUpgradeUrl({
                  returnPath: `${location.pathname}${location.search}`,
                  source: 'resource_limit_gate',
                })
              )
            }}
            context={upgradeContext}
            currentTier={getCurrentTier()}
            nextTier={getNextTier() || 'pro'}
            usageInfo={{
              current: currentCount,
              limit: limitInfo.limit_status.limit,
              percentage: limitInfo.limit_status.percentage_used,
              warningLevel: limitInfo.limit_status.warning_level
            }}
          />
        </>
      )

    default:
      return null
  }
}

/**
 * Tier Badge Component
 * Shows which tier is required for a feature
 */
export function TierRequiredBadge({
  feature,
  className = ""
}: {
  feature: string
  className?: string
}) {
  const { checkFeatureAccess } = useTier()
  const [featureInfo, setFeatureInfo] = useState<any>(null)
  const { t } = useTranslation()

  useEffect(() => {
    async function checkFeature() {
      const info = await checkFeatureAccess(feature)
      setFeatureInfo(info)
    }
    checkFeature()
  }, [feature, checkFeatureAccess])

  if (!featureInfo || featureInfo.has_access) {
    return null
  }

  return (
    <Badge variant="outline" className={`${className} text-xs`}>
      {t('featureGate.tierRequired', { tier: featureInfo.required_tier || 'Pro', ns: 'common' }) as string}
    </Badge>
  )
}

/**
 * Usage Warning Component
 * Shows warnings when approaching limits
 */
export function UsageWarning({
  resourceType,
  currentCount,
  className = ""
}: {
  resourceType: string
  currentCount: number
  className?: string
}) {
  const { getResourceLimits, getUsageWarning, loading } = useTier()
  const { t } = useTranslation()
  const [limitInfo, setLimitInfo] = useState<any>(null)
  const [isChecking, setIsChecking] = useState(false)

  useEffect(() => {
    // Don't check limits while tier info is still loading
    if (loading || isChecking) {
      return
    }

    async function checkLimits() {
      try {
        setIsChecking(true)
        const info = await getResourceLimits(resourceType, currentCount)
        setLimitInfo(info)
      } catch (error) {
        console.warn(`Failed to check ${resourceType} usage limits:`, error)
        // Fail silently for usage warnings - better to show no warning than break the UI
        setLimitInfo(null)
      } finally {
        setIsChecking(false)
      }
    }
    
    checkLimits()
  }, [resourceType, currentCount, getResourceLimits, loading, isChecking])

  // Don't show warnings while still loading or checking
  if (loading || isChecking || !limitInfo || limitInfo.limit_status.warning_level === 'info') {
    return null
  }

  const warning = getUsageWarning(
    resourceType,
    currentCount,
    limitInfo.limit_status.limit
  )

  if (!warning) return null

  const getWarningStyle = () => {
    switch (limitInfo.limit_status.warning_level) {
      case 'exceeded':
        return 'bg-red-50 border-red-200 text-red-800'
      case 'critical':
        return 'bg-orange-50 border-orange-200 text-orange-800'
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800'
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800'
    }
  }

  return (
    <div className={`${className} p-3 rounded-lg border ${getWarningStyle()}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{warning}</p>
        {limitInfo.upgrade_url && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.open(limitInfo.upgrade_url, '_blank')}
          >
            <Zap className="h-3 w-3 mr-1" />
            {t('navigation.upgrade', { ns: 'navigation' }) as string}
          </Button>
        )}
      </div>
    </div>
  )
}

/**
 * Higher-order component for feature gating
 */
export function withFeatureGate<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  feature: string,
  fallbackType: 'hidden' | 'disabled' | 'upgrade-prompt' = 'hidden'
) {
  return function FeatureGatedComponent(props: P) {
    return (
      <FeatureGate
        feature={feature}
        fallbackType={fallbackType}
      >
        <WrappedComponent {...props} />
      </FeatureGate>
    )
  }
}

/**
 * Hook for feature gating logic
 */
export function useFeatureGate(feature: string) {
  const { canAccess, getCurrentTier, getNextTier, requiresUpgrade } = useTier()
  
  return {
    hasAccess: canAccess(feature),
    requiresUpgrade: requiresUpgrade(feature),
    currentTier: getCurrentTier(),
    nextTier: getNextTier()
  }
} 